import React, { useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "@mui/material/styles";

interface ToastComponentProps {
  position?:
    | "top-right"
    | "top-left"
    | "top-center"
    | "bottom-right"
    | "bottom-left"
    | "bottom-center";
  autoClose?: number;
  hideProgressBar?: boolean;
  newestOnTop?: boolean;
  closeOnClick?: boolean;
  rtl?: boolean;
  pauseOnFocusLoss?: boolean;
  draggable?: boolean;
  pauseOnHover?: boolean;
}

const ToastComponent: React.FC<ToastComponentProps> = ({
  position = "top-right",
  autoClose = 30000,
  hideProgressBar = false,
  newestOnTop = false,
  closeOnClick = true,
  rtl = false,
  pauseOnFocusLoss = true,
  draggable = true,
  pauseOnHover = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const toastSurface = isDark ? theme.palette.dark.main : theme.palette.background.paper;
  const toastText = isDark ? theme.palette.common.white : theme.palette.text.primary;

  useEffect(() => {
    // Inject custom toast styles using theme colors
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .Toastify__toast {
        background-color: ${toastSurface} !important;
        color: ${toastText} !important;
        border: 1px solid ${theme.palette.primary.main}30 !important;
        border-radius: 8px !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
      }
      .Toastify__toast-body {
        background-color: ${toastSurface} !important;
        color: ${toastText} !important;
      }
      .Toastify__progress-bar {
        background-color: ${theme.palette.primary.main} !important;
      }
      .Toastify__close-button {
        color: ${toastText} !important;
      }
      .Toastify__toast--success {
        background-color: ${toastSurface} !important;
        border-color: ${theme.palette.success.main}30 !important;
      }
      .Toastify__toast--error {
        background-color: ${toastSurface} !important;
        border-color: ${theme.palette.error.main}30 !important;
      }
      .Toastify__toast--warning {
        background-color: ${toastSurface} !important;
        border-color: ${theme.palette.warning.main}30 !important;
      }
      .Toastify__toast--info {
        background-color: ${toastSurface} !important;
        border-color: ${theme.palette.info.main}30 !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [theme, toastSurface, toastText]);

  return (
    <ToastContainer
      position={position}
      autoClose={autoClose}
      hideProgressBar={hideProgressBar}
      newestOnTop={newestOnTop}
      closeOnClick={closeOnClick}
      rtl={rtl}
      pauseOnFocusLoss={pauseOnFocusLoss}
      draggable={draggable}
      pauseOnHover={pauseOnHover}
      theme={isDark ? "dark" : "light"}
    />
  );
};

export default ToastComponent;
